import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useDispatch } from "react-redux";
import useApi from "../../services/useApi";
import endpoints from "../../services/endpoints";
import { showToast } from "../toastMessage/toastSlice";
import Button from "../buttons/Button";
import ExchangeRateModal, { ActiveExchangeRate } from "./ExchangeRateModal";

// Fallback used only until the API reports its own threshold.
const DEFAULT_WARNING_THRESHOLD = 3;

/**
 * The USD -> AFN exchange rate summary shown at the top of the dashboard, with
 * an Edit action that opens the rate modal. Available to any authenticated
 * admin — the endpoints are gated by the Cognito authorizer alone, with no
 * per-feature privilege check.
 */
const ExchangeRatePill: React.FC = () => {
  const api = useApi();
  const dispatch = useDispatch();

  const [activeRate, setActiveRate] = useState<ActiveExchangeRate | null>(null);
  const [warningThreshold, setWarningThreshold] = useState<number>(DEFAULT_WARNING_THRESHOLD);
  const [fetching, setFetching] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  // useApi() returns fresh function identities every render, so the initial
  // fetch is guarded by a ref rather than a dependency array (the same pattern
  // the dashboard uses for its other widgets).
  const hasFetched = useRef(false);

  const fetchActiveRate = useCallback(async () => {
    setFetching(true);
    const response = await api.getAPI(endpoints.activeExchangeRate);
    if (!response?.error) {
      setActiveRate(response?.data?.data ?? null);
      if (typeof response?.data?.deviation_warning_percent === "number") {
        setWarningThreshold(response.data.deviation_warning_percent);
      }
    }
    setFetching(false);
  }, [api]);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchActiveRate();
  }, [fetchActiveRate]);

  const handleSave = async (rate: number) => {
    setSaving(true);
    const response = await api.PostAPI(endpoints.exchangeRate, { rate });
    setSaving(false);

    if (response?.error) {
      dispatch(
        showToast({
          type: "error",
          message:
            response?.data?.response?.data?.message ||
            "Unable to update the exchange rate. Please try again.",
        })
      );
      return;
    }

    dispatch(
      showToast({
        type: "success",
        message: response?.data?.message || "Exchange rate updated successfully",
      })
    );
    setIsModalOpen(false);
    // Re-read rather than trusting the local value, so the pill always shows
    // what the server actually has active.
    await fetchActiveRate();
  };

  const fromCurrency = activeRate?.from_currency || "USD";
  const toCurrency = activeRate?.to_currency || "AFN";

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        data-testid="exchange-rate-pill"
        className="flex items-center gap-1.5 border border-secondary_text rounded-xl bg-white p-2 w-fit"
      >
        <div className="flex items-center gap-1.5">
          <div className="flex flex-col justify-center gap-px h-[47px] w-[100px] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-2 py-[5px] overflow-hidden">
            <p className="text-[10px] font-semibold leading-normal text-[#6E7378]">From</p>
            <p className="text-[14px] font-bold leading-normal text-primary_text whitespace-nowrap">
              1 {fromCurrency}
            </p>
          </div>

          {/* Arrow stacked above the green "Current Rate" tag; the fixed 92px
              column keeps the tag from stretching with the currency labels. */}
          <div className="flex flex-col items-center gap-0.5 w-[92px]">
            <svg
              className="h-5 w-5 shrink-0"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <rect width="20" height="20" rx="10" fill="#E7EDF5" />
              <path
                d="M7.08594 10.0029H12.9199M10.0029 12.9199L12.9199 10.0029L10.0029 7.08594"
                stroke="#144EA2"
                strokeWidth="1.25"
                strokeLinecap="round"
              />
            </svg>
            <span className="flex items-center justify-center gap-1 w-full bg-[#DCFCE7] rounded-md px-2 py-1 overflow-hidden">
              <span className="h-1.5 w-1.5 rounded-full bg-[#16A34A] shrink-0" aria-hidden="true" />
              <span className="text-[10px] font-semibold leading-normal text-[#16A34A] whitespace-nowrap">
                Current Rate
              </span>
            </span>
          </div>

          <div className="flex flex-col justify-center gap-px h-[47px] w-[100px] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-2 py-[5px] overflow-hidden">
            <p className="text-[10px] font-semibold leading-normal text-[#6F7378]">To</p>
            <p className="text-[14px] font-bold leading-normal text-primary_text whitespace-nowrap">
              {fetching ? "—" : activeRate ? `${activeRate.rate} ${toCurrency}` : "Not set"}
            </p>
          </div>
        </div>

        {/* Fixed 66px so the card lands on the designed 394px total; the
            label's intrinsic width renders 8px narrower than the design's. */}
        <Button
          buttonName="Edit"
          className="px-4 py-[7px] rounded-lg"
          buttonWidth="w-[66px]"
          buttonHight="h-[39px]"
          textSize="text-[14px] font-semibold"
          buttonOnclick={() => setIsModalOpen(true)}
          buttonDisabled={fetching}
          testId="exchange-rate-edit-btn"
        />
      </motion.div>

      {isModalOpen && (
        <ExchangeRateModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          activeRate={activeRate}
          warningThreshold={warningThreshold}
          loading={saving}
          onSave={handleSave}
        />
      )}
    </>
  );
};

export default ExchangeRatePill;
